'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import type { MultiSelectValue, InquiryFlowSnapshot } from '@miso/design-system';
import { isCustomMultiSelectItem, multiSelectItemId } from '@miso/design-system';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { RecordCustomerRequestsRequest } from '@/entities/visit-counsel/model/intake.types';
import { createProgressFlowFetcher } from '@/shared/api/inquiry-flow/fetchProgressFlow';
import { ensureIntakeSessionStarted } from '../model/ensureIntakeSessionStarted';
import {
  CUSTOMER_REQUESTS_CUSTOM_TRIGGER,
  CUSTOMER_REQUESTS_PAID_QUESTION_ID,
  CUSTOMER_REQUESTS_QUESTION_ID,
  CR_QUESTION_PREDEFINED_VALUES,
  CR_PAID_PREDEFINED_VALUES,
} from '../model/staticFlows';
import { StepFlow } from './StepFlow';

const Q_QUESTIONS = CUSTOMER_REQUESTS_QUESTION_ID;
const Q_PAID = CUSTOMER_REQUESTS_PAID_QUESTION_ID;

const CUSTOM_TRIGGER = CUSTOMER_REQUESTS_CUSTOM_TRIGGER;

const FLOW_ID = 'progress-customer-requests';

interface Props {
  visitCounselId: string;
  counsel: VisitCounselView;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  onAfterDone: (counsel: VisitCounselView) => void;
  onCancel: () => void;
  progressOffset?: number;
  progressTotal?: number;
  onStateChange?: (snap: InquiryFlowSnapshot) => void;
}

/**
 * 추가 요청사항 + 비교견적 step (Figma `225:16437` ~ `225:16642`).
 *
 * 두 multi-select extensible 질문을 한 InquiryFlow 안에서 순차 진행한다.
 * 각 질문에서 "직접 입력" 옵션을 선택하면 BottomSheet 가 열려 자유 텍스트를 추가
 * 항목으로 등록 (multi-select extensible 패턴 — design-system 기존 지원).
 *
 * 저장 시 `recordCustomerRequests` 한 번 호출.
 *
 * 후속 작업: 비교견적의 분기(`반포장 이사`만 노출 등)는 `skipIf` 로 정교화 가능.
 */
export function CustomerRequestsStepAdapter({
  visitCounselId,
  counsel,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
  progressOffset,
  progressTotal,
  onStateChange,
}: Props) {
  // Firestore `playground_flows/progress-customer-requests` 의 FlowDocument 를 비동기 로드.
  const fetcher = useMemo(() => createProgressFlowFetcher(FLOW_ID), []);

  const initialValues = useMemo(() => {
    const cr = counsel.customerRequests;
    if (!cr) return undefined;
    return {
      [Q_QUESTIONS]: restoreMultiSelectValue(cr.questions ?? [], CR_QUESTION_PREDEFINED_VALUES),
      [Q_PAID]: restoreMultiSelectValue(cr.paidQuoteRequests ?? [], CR_PAID_PREDEFINED_VALUES),
    };
  }, [counsel.customerRequests]);

  // 마운트 시점 intake-session 보장.
  useEffect(() => {
    void ensureIntakeSessionStarted(visitCounselId, counsel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = useCallback(
    async (values: Record<string, unknown>) => {
      const questions = multiSelectToStringArray(values[Q_QUESTIONS]);
      const paid = multiSelectToStringArray(values[Q_PAID]);

      const request: RecordCustomerRequestsRequest = {
        questions,
        paidQuoteRequests: paid,
      };

      try {
        await ensureIntakeSessionStarted(visitCounselId, counsel);
        await myVisitCounselApi.recordCustomerRequests(visitCounselId, request);
        showToast('요청사항이 저장되었습니다.', 'success');
        const refetched = await refetch();
        const updated = refetched?.counsel ?? null;
        if (updated) onAfterDone(updated);
        else onCancel();
      } catch (err) {
        showToast(err instanceof Error ? err.message : '요청사항 저장에 실패했습니다.', 'error');
      }
    },
    [visitCounselId, counsel, showToast, refetch, onAfterDone, onCancel]
  );

  return (
    <StepFlow
      fetcher={fetcher}
      initialValues={initialValues}
      onComplete={handleComplete}
      onCancel={onCancel}
      onError={(err) => showToast(err.message ?? '플로우 오류가 발생했습니다.', 'error')}
      progressOffset={progressOffset}
      progressTotal={progressTotal}
      onStateChange={onStateChange}
    />
  );
}

/**
 * 저장된 string[] → MultiSelectValue 복원.
 *
 * 저장 시 사전 정의 옵션은 value 문자열로, 직접입력 항목은 label 문자열로 저장된다.
 * 복원 시 predefinedValues 에 없는 문자열은 CustomMultiSelectItem 으로 변환해야
 * MultiSelectRenderer 가 mergedOptions 에 포함시키고 화면에 표시할 수 있다.
 */
function restoreMultiSelectValue(
  stored: string[],
  predefinedValues: Set<string>
): MultiSelectValue {
  return stored.map((s) => {
    if (predefinedValues.has(s)) return s;
    return { id: `custom_restored_${s}`, label: s, custom: true as const };
  });
}

/** multi-select 답변 (string | CustomMultiSelectItem)[] → string[] (라벨 또는 옵션 value) */
function multiSelectToStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const items = value as MultiSelectValue;
  return items
    .map((item) => {
      if (isCustomMultiSelectItem(item)) return item.label;
      // CUSTOM_TRIGGER 자체는 sentinel 이므로 저장 대상에서 제외.
      const id = multiSelectItemId(item);
      return id === CUSTOM_TRIGGER ? '' : id;
    })
    .filter((s) => s !== '');
}
